var config                   = require("app/config"),
    get                      = require("object-path").get,
    quickMacroize            = require("enyo/macroize").quickMacroize,
    shuffleArray             = require("app/Domain/Ott/Api/V2/DataTransformers/shuffleArray"),
    transformAncestor,
    transformCategory,
    transformCategoryItem,
    transformCredits         = require("app/Domain/Ott/Api/V2/DataTransformers/transformCredits"),
    transformMediaHeader     = require("app/Domain/Ott/Api/V2/DataTransformers/transformMediaHeader"),
    transformMediaLinks      = require("app/Domain/Ott/Api/V2/DataTransformers/transformMediaLinks"),
    transformMediaMetaSlides = require("app/Domain/Ott/Api/V2/DataTransformers/transformMediaMetaSlides"),
    transformProduct,
    transformProductCategory;

/**
 * Takes raw incoming data from the API and applies some extra sugar.
 *
 * @param    {string}  language  The current application language
 * @param    {Object}  audio     The audio data
 * @returns  {Object}  The transformed data
 * @public
 */
exports.transformAudio = function(language, audio) {
    // Temporary injection of additional media slider data
    audio = transformMediaHeader(audio);

    // Clean up the slides
    audio = transformMediaMetaSlides(language, audio);

    // Transform the Credits
    audio = transformCredits(audio);

    // Add links
    audio = transformMediaLinks(language, audio);

    // Dashboard
    audio.dashboard = audio.dashboard || [];
    audio.dashboard.map(function(category) {
        switch (category.type) {
            case "product_category":
                return transformProductCategory(language, audio, category, config.maxCategoryLength);
            case "category":
                return transformCategory(language, audio, category, config.maxCategoryLength);
            default:
                return category;
        }
    });

    // Reject any categories without content
    audio.dashboard = audio.dashboard.filter(function(category) {
        switch (category.type) {
            case "product_category":
                return get(category, "children.length", 0) > 0;
            case "category":
                return get(category, "category_content.length", 0) > 0;
            default:
                return false;
        }
    });

    return audio;
};

/**
 * Adds links to product category, transforms nested data.
 *
 * @param    {string}  language         The current application language
 * @param    {Object}  audio            The audio data
 * @param    {Object}  productCategory  The product category data
 * @param    {Number}  maxLength        The maximum amount of items in the
 *                                      category
 * @returns  {Object}  The transformed data
 * @private
 */
transformProductCategory = function(language, audio, productCategory, maxLength) {
    // Add links
    productCategory = transformMediaLinks(language, productCategory);

    // Ensure presence of content array
    productCategory.children = productCategory.children || [];

    // Limit the amount of items in the array and add a 'view more' record as
    // needed.
    if (productCategory.children.length > maxLength) {

        // Trim array
        productCategory.children.length = maxLength - 1;

        // Add 'view more' item
        productCategory.children.push({
            type:  "view_more",
            links: {
                self: productCategory.links.self
            }
        });
    }

    // Transform category content
    productCategory.children.map(function(child) {
        switch (child.type) {
            case "product_category":
                return transformProductCategory(language, audio, child, maxLength);
            case "product":
                return transformProduct(language, child);
            default:
                return child;
        }
    });

    return productCategory;
};

/**
 * Adds links to product data.
 *
 * @param    {string}  language  The current application language
 * @param    {Object}  product   The product data
 * @returns  {Object}  The transformed data
 * @private
 */
transformProduct = function(language, product) {
    product = transformMediaLinks(language, product);

    return product;
};

/**
 * Adds links to category, transforms nested data.
 *
 * @param    {string}  language   The current application language
 * @param    {Object}  audio      The audio data
 * @param    {Object}  category   The category data
 * @param    {Number}  maxLength  The maximum amount of items in the category
 * @returns  {Object}  The transformed data
 * @private
 */
transformCategory = function(language, audio, category, maxLength) {
    // Add link
    category = transformMediaLinks(language, category);

    // Ensure presence of content array
    category.category_content = category.category_content || [];

    // Sorting
    switch (get(category, "category_layout.sort", "default")) {
        case "random":
            category.category_content = shuffleArray(category.category_content);
            break;
        default:
            break;
    }

    // Limit the amount of items in the array and add a 'view more' record as
    // needed.
    if (category.category_content.length > maxLength) {
        // Category page enabled, add view-more thumb
        if (get(category, "page_enabled", true)) {
            category.category_content.length = maxLength - 1;
            category.category_content.push({
                type:  "view_more",
                links: {
                    self: category.links.self
                }
            });
        }

        // No category page, no view-more thumb
        else {
            category.category_content.length = maxLength;
        }
    }

    // Transform category content
    category.category_content.map(function(content) {
        switch (content.type) {
            case "album_track":
            case "artist_page":
            case "audio":
            case "book":
            case "brand":
            case "category":
            case "episode":
            case "external":
            case "issue":
            case "magazine":
            case "music_album":
            case "page":
            case "photo":
            case "playlist_page":
            case "podcast":
            case "podcast_episode":
            case "product":
            case "product_category":
            case "series":
            case "video":
                return transformCategoryItem(language, audio, category, content);
            default:
                return content;
        }
    });

    return category;
};

/**
 * Adds links to category media item data.
 *
 * @param    {string}  language   The current application language
 * @param    {Object}  audio      The audio data
 * @param    {Object}  category   The category data
 * @param    {Object}  media      The mediaItem data
 * @returns  {Object}  The transformed data
 * @private
 */
transformCategoryItem = function(language, audio, category, media) {
    // Add links
    media = transformMediaLinks(language, media, {
        categorySlug: get(category, "meta.slug", null)
    });

    switch (media.type) {
        case "album_track":
        case "audio":
        case "book":
        case "episode":
        case "issue":
        case "podcast_episode":
        case "video":
            media.links.details = quickMacroize("/{$language}/audio/{$audioSlug}/{$mediaType}/{$mediaSlug}", {
                language:  language,
                audioSlug: get(audio, "meta.slug", null),
                mediaType: media.type.replaceAll("_", "-"),
                mediaSlug: get(media, "meta.slug", null)
            });
            break;
        case "artist_page":
        case "brand":
        case "category":
        case "external":
        case "magazine":
        case "music_album":
        case "page":
        case "photo":
        case "playlist_page":
        case "podcast":
        case "product":
        case "product_category":
        case "series":
        default:
            break;
    }

    if (media.parent && typeof media.parent === "object") {
        media.parent = transformAncestor(language, media.parent);
    }

    return media;
};

/**
 * Adds links to ancestor.
 *
 * @param    {string}  language  The current application language
 * @param    {Object}  media     The media data
 * @returns  {Object}  The transformed data
 * @public
 */
transformAncestor = function(language, media) {
    media = transformMediaLinks(language, media);

    if (media.parent && typeof media.parent === "object") {
        media.parent = transformAncestor(language, media.parent);
    }

    return media;
};
